import { debug } from "./console";
import { isSameRace } from "./raceValueUtils";
import { parseDatasetEventTags } from "./datasetEventTags";
import { raceNumberFromExploreTags } from "./taggerEventEditForm";
import {
  selectedLegsTimeseries,
  selectedRacesTimeseries,
} from "../store/filterStore";
import {
  isPlaying,
  releaseTimeControl,
  requestTimeControl,
  setIsManualTimeChange,
  setSelectedTime,
  timeWindow,
} from "../store/playbackStore";

const MAP_OVERLAY_MENUS = ["MAP", "OVERLAY"] as const;

/** Default/max playback window (minutes) when fleet day has no real race context. */
export const FLEET_NO_RACE_DEFAULT_TIME_WINDOW_MIN = 30;
export const FLEET_NO_RACE_MAX_TIME_WINDOW_MIN = 30;
/** Brush selections wider than this (minutes) switch playback to Full/Brush (0). */
export const FLEET_BRUSH_OVERRIDE_THRESHOLD_MIN = 30;
/**
 * Fleet map playback parks this far into the race/data on page init and whenever a race is chosen.
 * Matches REFERENCE_DELTA_WARMUP_MS (components/charts/map/utils/vmgReferenceDelta) so the playhead
 * lands exactly where the meters-vs-reference trace starts moving, past the start-line scramble.
 */
export const FLEET_PLAYBACK_START_OFFSET_MS = 2 * 60 * 1000;

export const FLEET_TIME_WINDOW_CYCLE = [0.5, 1, 2, 5, 15, 30, 0] as const;
export const FLEET_NO_RACE_TIME_WINDOW_CYCLE = [0.5, 1, 2, 5, 15, 30] as const;

export type FleetBrushRange = {
  start_time?: string | Date;
  end_time?: string | Date;
};

/** Fleet day race fetch lifecycle for time-window gating. */
export type FleetRaceModeStatus = "pending" | "race" | "training" | "none" | "error";

export type FleetDateRacesRow = {
  race_number?: number | string;
  Race_number?: number | string;
  racenumber?: number | string;
  RaceNumber?: number | string;
  raceNumber?: number | string;
  HOUR?: number | string | null;
  [key: string]: unknown;
};

export type ClassifiedFleetRaceFetch = {
  status: Exclude<FleetRaceModeStatus, "pending">;
  trainingHourMode: boolean;
  raceOptions: string[];
  initialSelection: string[];
};

function extractPositiveFleetRaceNumbers(rows: FleetDateRacesRow[]): string[] {
  const extracted = rows
    .map((row) => {
      const raceNum =
        row.race_number ??
        row.Race_number ??
        row.racenumber ??
        row.RaceNumber ??
        row["Race_number"] ??
        row.raceNumber;
      if (raceNum === -1 || raceNum === "-1") return null;
      const parsed = Number(raceNum);
      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      return String(Math.trunc(parsed));
    })
    .filter((value): value is string => value != null && value !== "");
  return [...new Set(extracted)].sort((a, b) => Number(a) - Number(b));
}

function extractFleetTrainingHourKeys(rows: FleetDateRacesRow[]): string[] {
  const hourKeys = rows
    .map((row) =>
      row?.HOUR != null && String(row.HOUR).trim() !== "" ? String(row.HOUR) : null
    )
    .filter((key): key is string => key != null && key !== "");
  return [...new Set(hourKeys)].sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return String(a).localeCompare(String(b));
  });
}

/**
 * Classify /date/races response: positive race numbers win over training-hour labels.
 */
export function classifyFleetDateRacesResponse(
  racesList: unknown
): ClassifiedFleetRaceFetch {
  const rows = Array.isArray(racesList) ? (racesList as FleetDateRacesRow[]) : [];
  const positiveRaces = extractPositiveFleetRaceNumbers(rows);
  if (positiveRaces.length > 0) {
    return {
      status: "race",
      trainingHourMode: false,
      raceOptions: positiveRaces,
      initialSelection: [positiveRaces[0]],
    };
  }

  const hourKeys = extractFleetTrainingHourKeys(rows);
  if (hourKeys.length > 0) {
    return {
      status: "training",
      trainingHourMode: true,
      raceOptions: hourKeys,
      initialSelection: [hourKeys[0]],
    };
  }

  return {
    status: "none",
    trainingHourMode: false,
    raceOptions: [],
    initialSelection: [],
  };
}

/**
 * Fleet map time window after a race-filter change: keep the window the user already picked.
 *
 * Choosing a race chip used to slam the window to Full (0); it now preserves `currentWindowMinutes`,
 * including Full itself. The only substitution is when Full is not actually available (training-hour
 * mode or no race chip, where the cycle hides Full) — then 0 becomes the 30-minute default and
 * anything longer is capped to it.
 */
export function fleetTimeWindowWhenRaceFilterApplied(
  trainingHourMode: boolean,
  selectedRaces: string[],
  currentWindowMinutes: number
): number {
  const current = Number(currentWindowMinutes);
  const hasCurrent = Number.isFinite(current) && current >= 0;
  const realRace = !trainingHourMode && Array.isArray(selectedRaces) && selectedRaces.length > 0;

  if (realRace) {
    return hasCurrent ? current : 0;
  }
  if (hasCurrent && current > 0) {
    return Math.min(current, FLEET_NO_RACE_MAX_TIME_WINDOW_MIN);
  }
  return FLEET_NO_RACE_DEFAULT_TIME_WINDOW_MIN;
}

/** Stable signature for race-filter time-window defaulting (selection + training mode only). */
export function fleetRaceFilterSignature(
  trainingHourMode: boolean,
  selectedRaces: string[]
): string {
  const sel = Array.isArray(selectedRaces) ? selectedRaces.join(",") : "";
  return `${trainingHourMode ? "t" : "r"}:${sel}`;
}

/** Hide Full in fleet map cycle when no race chip is active or in training-hour mode. */
export function shouldHideFleetFullForRaceFilter(
  trainingHourMode: boolean,
  selectedRaces: string[]
): boolean {
  return trainingHourMode || !Array.isArray(selectedRaces) || selectedRaces.length === 0;
}

/** Hide Full only after race fetch resolves; keep Full available while pending. */
export function shouldHideFleetFullTimeWindowForMode(
  raceModeStatus: FleetRaceModeStatus,
  trainingHourMode: boolean,
  _raceOptions: (number | string)[],
  selectedRaces: string[] = []
): boolean {
  if (raceModeStatus === "pending") return false;
  return shouldHideFleetFullForRaceFilter(trainingHourMode, selectedRaces);
}

/** Default fleet time window once race mode is known; null while fetch is pending. */
export function defaultFleetTimeWindowWhenRaceModeReady(options: {
  raceModeStatus: FleetRaceModeStatus;
  trainingHourMode: boolean;
  raceOptions: (number | string)[];
  selectedRaces: string[];
}): number | null {
  if (
    !options.trainingHourMode &&
    Array.isArray(options.selectedRaces) &&
    options.selectedRaces.length > 0
  ) {
    return 0;
  }
  if (options.raceModeStatus === "pending") return null;
  return defaultFleetTimeWindowForRaceContext({
    trainingHourMode: options.trainingHourMode,
    raceOptions: options.raceOptions,
    selectedRaces: options.selectedRaces,
  });
}

/** True when a real race is selected (not training-hour mode). */
export function hasRealRaceSelected(
  raceOptions: (number | string)[],
  selectedRaces: string[],
  trainingHourMode: boolean
): boolean {
  if (trainingHourMode) return false;
  if (!Array.isArray(selectedRaces) || selectedRaces.length === 0) return false;

  const options = Array.isArray(raceOptions) ? raceOptions : [];
  if (options.length === 0) {
    // Race options not loaded yet — trust non-empty selection on non-training days.
    return true;
  }

  return selectedRaces.some((sel) =>
    options.some((opt) => isSameRace(opt, sel))
  );
}

/** True when the Full (0) option is available in the fleet map time-window cycle. */
export function isFleetFullTimeWindowAvailable(
  trainingHourMode: boolean,
  raceOptions: (number | string)[],
  selectedRaces: string[] = []
): boolean {
  return hasRealRaceSelected(raceOptions, selectedRaces, trainingHourMode);
}

/** Hide Full in the time-window cycle when no real race is selected. */
export function shouldHideFleetFullTimeWindow(
  trainingHourMode: boolean,
  raceOptions: (number | string)[],
  selectedRaces: string[] = []
): boolean {
  return !hasRealRaceSelected(raceOptions, selectedRaces, trainingHourMode);
}

export function getBrushDurationMinutes(
  ranges: FleetBrushRange[] | undefined | null
): number | null {
  if (!Array.isArray(ranges) || ranges.length === 0) return null;
  const item = ranges[0];
  if (!item?.start_time || !item?.end_time) return null;
  const start =
    item.start_time instanceof Date ? item.start_time : new Date(item.start_time);
  const end = item.end_time instanceof Date ? item.end_time : new Date(item.end_time);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return Math.abs(end.getTime() - start.getTime()) / (60 * 1000);
}

export function isLongBrushSelection(ranges: FleetBrushRange[] | undefined | null): boolean {
  const duration = getBrushDurationMinutes(ranges);
  return duration != null && duration > FLEET_BRUSH_OVERRIDE_THRESHOLD_MIN;
}

/**
 * Resolve the initial fleet map time window: the user's saved setting wins whenever it is usable in
 * the current race context, otherwise the context default (Full with a real race, else 30 min).
 *
 * A saved Full (0) only survives when a real race is selected — Full is hidden from the cycle
 * otherwise — and saved positive values must be one of the cycle steps.
 */
export function resolveFleetInitialTimeWindow(options: {
  trainingHourMode: boolean;
  raceOptions: (number | string)[];
  selectedRaces: string[];
  savedTimeWindow?: string | null;
}): number {
  const fullAvailable = hasRealRaceSelected(
    options.raceOptions,
    options.selectedRaces,
    options.trainingHourMode
  );
  const contextDefault = fullAvailable ? 0 : FLEET_NO_RACE_DEFAULT_TIME_WINDOW_MIN;

  const allowed = FLEET_NO_RACE_TIME_WINDOW_CYCLE as readonly number[];
  const saved = options.savedTimeWindow;
  if (saved != null && saved !== "") {
    const parsed = parseFloat(saved);
    if (Number.isFinite(parsed)) {
      if (parsed === 0) {
        return contextDefault; // saved Full: honored only where Full exists, which contextDefault encodes
      }
      if (parsed > 0 && allowed.includes(parsed)) {
        return parsed;
      }
    }
  }
  return contextDefault;
}

/** Time window (minutes) saved when entering brush mode; restored on brush clear. */
let fleetPreBrushTimeWindowMinutes: number | null = null;

/** Reset stored pre-brush window (e.g. tests or explicit user time-window change). */
export function resetFleetPreBrushTimeWindow(): void {
  fleetPreBrushTimeWindowMinutes = null;
}

/** Enter brush mode: remember current window and return 0 (Brush). */
export function fleetTimeWindowEnterBrush(currentWindowMinutes: number): number {
  if (Number.isFinite(currentWindowMinutes) && currentWindowMinutes > 0) {
    fleetPreBrushTimeWindowMinutes = currentWindowMinutes;
  }
  return 0;
}

/** Leave brush mode: restore pre-brush window when still at 0, else keep current. */
export function fleetTimeWindowLeaveBrush(currentWindowMinutes: number): number {
  const stored = fleetPreBrushTimeWindowMinutes;
  fleetPreBrushTimeWindowMinutes = null;
  if (
    stored != null &&
    stored > 0 &&
    Number.isFinite(currentWindowMinutes) &&
    currentWindowMinutes === 0
  ) {
    return stored;
  }
  return Number.isFinite(currentWindowMinutes) ? currentWindowMinutes : 0;
}

/** Time window (minutes) to set after a brush; any brush becomes Brush mode (0). */
export function fleetTimeWindowAfterBrush(
  brushRanges: FleetBrushRange[],
  currentWindowMinutes: number
): number {
  if (Array.isArray(brushRanges) && brushRanges.length > 0) {
    return fleetTimeWindowEnterBrush(currentWindowMinutes);
  }
  return fleetTimeWindowLeaveBrush(currentWindowMinutes);
}

/** Default time window when fleet race context changes (load or chip change). */
export function defaultFleetTimeWindowForRaceContext(options: {
  trainingHourMode: boolean;
  raceOptions: (number | string)[];
  selectedRaces: string[];
}): number {
  if (
    hasRealRaceSelected(
      options.raceOptions,
      options.selectedRaces,
      options.trainingHourMode
    )
  ) {
    return 0;
  }
  return FLEET_NO_RACE_DEFAULT_TIME_WINDOW_MIN;
}

/** @deprecated Use defaultFleetTimeWindowForRaceContext */
export const fleetTimeWindowForRaceSelection = defaultFleetTimeWindowForRaceContext;

/** Stable signature for race-context time-window defaulting. */
export function fleetRaceTimeWindowContextSignature(options: {
  trainingHourMode: boolean;
  raceOptions: (number | string)[];
  selectedRaces: string[];
}): string {
  const mode = options.trainingHourMode ? "t" : "r";
  const opts = Array.isArray(options.raceOptions) ? options.raceOptions.join(",") : "";
  const sel = Array.isArray(options.selectedRaces) ? options.selectedRaces.join(",") : "";
  return `${mode}:${opts}|${sel}`;
}

/**
 * Playback minutes for fleet map + fleet time series when map scope is active.
 * Honors store timeWindow; 0 means Full/Brush. Caps at 30 min only without real races.
 */
export function effectiveFleetPlaybackWindowMinutes(input: {
  scopeActive: boolean;
  windowMinutes: number;
  trainingHourMode: boolean;
  raceOptions: (number | string)[];
  selectedRaces: string[];
  selectedRange?: FleetBrushRange[];
}): number {
  if (!input.scopeActive) return 0;

  if (isLongBrushSelection(input.selectedRange)) {
    return 0;
  }

  const minutes = Number(input.windowMinutes);
  const fullAvailable = isFleetFullTimeWindowAvailable(
    input.trainingHourMode,
    input.raceOptions,
    input.selectedRaces
  );

  if (Number.isFinite(minutes) && minutes === 0) {
    return fullAvailable ? 0 : FLEET_NO_RACE_DEFAULT_TIME_WINDOW_MIN;
  }

  if (fullAvailable && Number.isFinite(minutes) && minutes > 0) {
    return minutes;
  }

  if (!Number.isFinite(minutes) || minutes <= 0) {
    return FLEET_NO_RACE_DEFAULT_TIME_WINDOW_MIN;
  }
  return Math.min(minutes, FLEET_NO_RACE_MAX_TIME_WINDOW_MIN);
}

export type MapScopeContext = {
  /** Fleet day map instances mounted (split, popout, or child window). */
  fleetMapMountedCount?: number;
  /** Dataset explore map instances mounted. */
  exploreMapMountedCount?: number;
  isInSplitView?: boolean;
  mainPanelMenu?: string | null;
  rightPanelMenu?: string | null;
};

/** @deprecated Use MapScopeContext — kept for existing fleet report imports. */
export type FleetTimeWindowContext = MapScopeContext;

export type FleetActiveTimeWindow = {
  start: Date;
  end: Date;
};

function normalizePanelMenu(menu: string | null | undefined): string {
  return String(menu ?? "").trim().toUpperCase();
}

/** True when MAP/OVERLAY is in the current split layout panels. */
export function isMapInSplitLayout(
  ctx: MapScopeContext,
  menus: readonly string[] = MAP_OVERLAY_MENUS
): boolean {
  if (!ctx.isInSplitView) return false;
  const main = normalizePanelMenu(ctx.mainPanelMenu);
  const right = normalizePanelMenu(ctx.rightPanelMenu);
  return menus.some((m) => m === main || m === right);
}

/** True when MAP/OVERLAY is open in the current split layout (not merely mounted elsewhere). */
export function isFleetMapVisibleContext(ctx: MapScopeContext): boolean {
  return isMapInSplitLayout(ctx, MAP_OVERLAY_MENUS);
}

/** True when FleetMap drives fleet reports: split MAP/OVERLAY or a mounted FleetMap in another window. */
export function isFleetMapActiveForReports(ctx: MapScopeContext): boolean {
  const mounted = Number(ctx.fleetMapMountedCount ?? 0);
  if (Number.isFinite(mounted) && mounted > 0) return true;
  return isFleetMapVisibleContext(ctx);
}

/**
 * True when FleetTimeSeries should follow FleetMap playback window.
 * Split MAP/OVERLAY sibling, or a live mounted FleetMap in another window — not stale standalone state.
 */
export function isFleetMapDrivingTimeSeries(ctx: MapScopeContext): boolean {
  return isFleetMapActiveForReports(ctx);
}

/** True when dataset explore map drives sibling explore pages (split MAP or mounted explore map). */
export function isExploreMapScopeActive(ctx: MapScopeContext): boolean {
  const mounted = Number(ctx.exploreMapMountedCount ?? 0);
  if (Number.isFinite(mounted) && mounted > 0) return true;
  return isMapInSplitLayout(ctx, MAP_OVERLAY_MENUS);
}

/** True when dataset explore map drives Performance/Maneuvers reports (split MAP or popout). */
export function isExploreMapActiveForReports(ctx: MapScopeContext): boolean {
  const mounted = Number(ctx.exploreMapMountedCount ?? 0);
  if (Number.isFinite(mounted) && mounted > 0) return true;
  return isMapInSplitLayout(ctx, MAP_OVERLAY_MENUS);
}

/** Dataset report sliding window — active when explore map is open in split or another window. */
export function computeExploreMapReportActiveTimeWindow(
  ctx: MapScopeContext,
  timeWindowMinutes: number,
  referenceTime: Date | null | undefined
): FleetActiveTimeWindow | null {
  return computeMapActiveTimeWindow(
    isExploreMapActiveForReports(ctx),
    timeWindowMinutes,
    referenceTime
  );
}

/** Playback minutes to apply; 0 when map scope is inactive (full range). */
export function effectiveMapTimeWindowMinutes(
  scopeActive: boolean,
  windowMinutes: number
): number {
  if (!scopeActive) return 0;
  const minutes = Number(windowMinutes);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
}

/** Quantize playback reference time so report filters do not re-run every animation frame. */
export function quantizeFleetReferenceTime(
  referenceTime: Date | null | undefined,
  quantizeMs = 1000
): Date | null {
  if (!referenceTime || !(referenceTime instanceof Date) || Number.isNaN(referenceTime.getTime())) {
    return null;
  }
  if (!Number.isFinite(quantizeMs) || quantizeMs <= 0) {
    return referenceTime;
  }
  return new Date(Math.floor(referenceTime.getTime() / quantizeMs) * quantizeMs);
}

/** Stable key for map window scoping — avoids redundant filter passes during smooth playback. */
export function fleetActiveTimeWindowScopeKey(
  window: FleetActiveTimeWindow | null,
  quantizeMs = 1000
): string {
  if (!window) return "full";
  const q = (t: Date) =>
    Math.floor(t.getTime() / (Number.isFinite(quantizeMs) && quantizeMs > 0 ? quantizeMs : 1));
  return `${q(window.start)}-${q(window.end)}`;
}

/** Sliding playback window (reference time minus timeWindow minutes). Null when scope inactive or full day. */
export function computeMapActiveTimeWindow(
  scopeActive: boolean,
  timeWindowMinutes: number,
  referenceTime: Date | null | undefined
): FleetActiveTimeWindow | null {
  if (!scopeActive) return null;
  if (!referenceTime || !(referenceTime instanceof Date) || Number.isNaN(referenceTime.getTime())) {
    return null;
  }
  const minutes = Number(timeWindowMinutes);
  if (!Number.isFinite(minutes) || minutes <= 0) return null;

  const end = referenceTime;
  const start = new Date(end.getTime() - minutes * 60 * 1000);
  return { start, end };
}

/** Fleet report sliding window — active when FleetMap is open in split or another window. */
export function computeFleetActiveTimeWindow(
  ctx: MapScopeContext,
  timeWindowMinutes: number,
  referenceTime: Date | null | undefined
): FleetActiveTimeWindow | null {
  return computeMapActiveTimeWindow(
    isFleetMapActiveForReports(ctx),
    timeWindowMinutes,
    referenceTime
  );
}

/** Explore chart sliding window — active when explore map is open in split or another window. */
export function computeExploreMapActiveTimeWindow(
  ctx: MapScopeContext,
  timeWindowMinutes: number,
  referenceTime: Date | null | undefined
): FleetActiveTimeWindow | null {
  return computeMapActiveTimeWindow(
    isExploreMapScopeActive(ctx),
    timeWindowMinutes,
    referenceTime
  );
}

/** Extract a row datetime for map window matching. */
export function extractFleetRowDatetime(row: Record<string, unknown> | undefined): Date | null {
  if (!row) return null;
  const raw =
    row.start_time ??
    row.Start_time ??
    row.datetime ??
    row.Datetime ??
    row.DATETIME ??
    row.date ??
    row.Date ??
    row.x;
  if (raw == null) return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  if (typeof raw === "string" && raw.trim() !== "") {
    const parsed = new Date(raw.replace(/ /g, "T"));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

export function rowWithinFleetActiveTimeWindow(
  row: Record<string, unknown>,
  window: FleetActiveTimeWindow | null
): boolean {
  if (!window) return true;
  const dt = extractFleetRowDatetime(row);
  if (!dt) return false;
  const t = dt.getTime();
  return t >= window.start.getTime() && t <= window.end.getTime();
}

export function filterRowsByFleetActiveTimeWindow<T extends Record<string, unknown>>(
  rows: T[],
  window: FleetActiveTimeWindow | null
): T[] {
  if (!window || !Array.isArray(rows) || rows.length === 0) return rows;
  const filtered = rows.filter((row) => rowWithinFleetActiveTimeWindow(row, window));
  if (filtered.length !== rows.length) {
    debug("[fleetTimeWindow] Scoped rows to map playback window", {
      before: rows.length,
      after: filtered.length,
      start: window.start.toISOString(),
      end: window.end.toISOString(),
    });
  }
  return filtered;
}

/** Scope rows to map playback window only when map scope is active. */
export function applyMapTimeScopeIfActive<T extends Record<string, unknown>>(
  rows: T[],
  scopeActive: boolean,
  window: FleetActiveTimeWindow | null
): T[] {
  if (!scopeActive) return rows;
  return filterRowsByFleetActiveTimeWindow(rows, window);
}

/** Signature of FleetMap race/leg filters (timeseries context) for fleet report sync. */
export function fleetMapRaceLegFilterSignature(): string {
  const races = selectedRacesTimeseries();
  const legs = selectedLegsTimeseries();
  return `r:${races.join(",")}|l:${legs.join(",")}`;
}

export type FleetMapRaceLegFilters = {
  races: string[];
  legs: string[];
};

export function readFleetMapRaceLegFilters(): FleetMapRaceLegFilters {
  return {
    races: selectedRacesTimeseries().map(String),
    legs: selectedLegsTimeseries().map(String),
  };
}

/** Training-hour chips are clock hours — do not sync as race_number filters. */
export function fleetReportRaceSelectionsFromMap(
  races: string[],
  trainingHourMode: boolean
): string[] {
  if (trainingHourMode) return [];
  return races.map((r) => String(r));
}

/** Map race chips → maneuver report selection (number or TRAINING string). */
export function parseFleetMapRaceSelections(races: string[]): (number | string)[] {
  return races.map((race) => {
    const trimmed = String(race).trim();
    if (!trimmed) return race;
    if (trimmed.toUpperCase() === "TRAINING") return "TRAINING";
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : trimmed;
  });
}

export function parseFleetMapLegSelections(legs: string[]): number[] {
  return legs.map((leg) => Number(leg)).filter((n) => Number.isFinite(n));
}

/** Stable scope signature for fleet/explore report refresh effects. */
export function mapReportScopeSignature(
  mapActive: boolean,
  windowKey: string
): string {
  return `${mapActive ? "1" : "0"}:${windowKey}`;
}

/**
 * Playback reference for freshly filtered data, clamped to the data's own end.
 *
 * With `startOffsetMs > 0` (fleet map: {@link FLEET_PLAYBACK_START_OFFSET_MS}) the target is
 * `dataStart + startOffsetMs` and the time window is irrelevant — the playhead parks a fixed distance
 * into the race whatever window the user has chosen. With no offset it falls back to the original
 * behaviour: the end of [dataStart, dataStart + timeWindow], so reports overlap the filtered data.
 */
export function computeFleetPlaybackTargetTime(
  rows: Record<string, unknown>[],
  timeWindowMinutes: number,
  startOffsetMs = 0
): Date | null {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  let minTime = Infinity;
  let maxTime = -Infinity;
  for (const row of rows) {
    const dt = extractFleetRowDatetime(row);
    if (!dt) continue;
    const t = dt.getTime();
    if (t < minTime) minTime = t;
    if (t > maxTime) maxTime = t;
  }
  if (!Number.isFinite(minTime)) return null;

  const offsetMs = Number(startOffsetMs);
  if (Number.isFinite(offsetMs) && offsetMs > 0) {
    return new Date(Math.min(maxTime, minTime + offsetMs));
  }

  const minutes = Number(timeWindowMinutes);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return new Date(minTime);
  }
  const windowEndMs = Math.min(maxTime, minTime + minutes * 60 * 1000);
  return new Date(windowEndMs);
}

/** Scope explore chart rows when dataset map is active in split or popout. */
export function scopeExploreChartData<T extends Record<string, unknown>>(
  rows: T[],
  ctx: MapScopeContext,
  referenceTime: Date | null | undefined,
  windowMinutes: number,
  playing: boolean
): T[] {
  const active = isExploreMapScopeActive(ctx);
  const ref = playing ? quantizeFleetReferenceTime(referenceTime, 1000) : referenceTime;
  const minutes = effectiveMapTimeWindowMinutes(active, windowMinutes);
  const window = computeMapActiveTimeWindow(active, minutes, ref);
  return applyMapTimeScopeIfActive(rows, active, window);
}

/**
 * Move playback reference when map race/leg filters change so split reports stay in sync.
 * `startOffsetMs` parks the playhead a fixed distance into the filtered data instead of at the end of
 * the time window — the fleet map passes {@link FLEET_PLAYBACK_START_OFFSET_MS}.
 */
export function repositionFleetPlaybackForFilteredData(
  rows: Record<string, unknown>[],
  controllerId: string,
  startOffsetMs = 0
): boolean {
  if (isPlaying()) return false;
  const target = computeFleetPlaybackTargetTime(rows, timeWindow(), startOffsetMs);
  if (!target || !Number.isFinite(target.getTime())) return false;
  if (!requestTimeControl(controllerId)) return false;

  setIsManualTimeChange(true);
  setSelectedTime(target, controllerId);
  setTimeout(() => releaseTimeControl(controllerId), 100);
  debug("[fleetTimeWindow] Repositioned playback for filtered race/leg data", {
    controllerId,
    target: target.toISOString(),
    rowCount: rows.length,
  });
  return true;
}

function fleetMapPointRaceNumber(pt: Record<string, unknown>): number | null {
  const rnum = Number(pt.Race_number ?? pt.Race ?? pt.race_number);
  return Number.isFinite(rnum) ? rnum : null;
}

function fleetMapPointLegNumber(pt: Record<string, unknown>): number | null {
  const lnum = Number(pt.leg_number ?? pt.Leg_number ?? pt.Leg ?? pt.LEG);
  return Number.isFinite(lnum) ? lnum : null;
}

/**
 * Filter fleet day map rows by active race/leg chips (same rules as MultiMapTimeSeries).
 * Returns the input unchanged when no race/leg filter applies or data has no matching fields.
 */
export function filterFleetMapPointsByRaceAndLeg(
  points: unknown[],
  options: {
    races: string[];
    legs: string[];
    trainingHourMode?: boolean;
  }
): unknown[] {
  if (!Array.isArray(points) || points.length === 0) {return [];}
  let filtered = points.filter((pt): pt is Record<string, unknown> => pt != null && typeof pt === 'object');
  const races = Array.isArray(options.races) ? options.races : [];
  const legs = Array.isArray(options.legs) ? options.legs : [];

  if (races.length > 0 && !options.trainingHourMode) {
    const chosen = races[0];
    if (chosen !== undefined && chosen !== null && String(chosen).trim() !== '') {
      const raceNumbersInData = [
        ...new Set(
          filtered
            .map((p) => fleetMapPointRaceNumber(p))
            .filter((n): n is number => n != null)
        ),
      ].sort((a, b) => a - b);

      if (raceNumbersInData.length > 0) {
        const chosenRaceNum = chosen === 'TRAINING' ? -1 : Number(chosen);
        if (Number.isFinite(chosenRaceNum)) {
          const raceToCheck = chosen === 'TRAINING' ? -1 : chosenRaceNum;
          if (raceNumbersInData.includes(raceToCheck)) {
            filtered = filtered.filter((p) => fleetMapPointRaceNumber(p) === raceToCheck);
          }
        }
      }
    }
  }

  if (legs.length > 0) {
    const legNumbers = legs.map((l) => Number(l)).filter((n) => Number.isFinite(n));
    if (legNumbers.length > 0) {
      filtered = filtered.filter((p) => {
        const lnum = fleetMapPointLegNumber(p);
        return lnum != null && legNumbers.includes(lnum);
      });
    }
  }

  return filtered;
}

/** Min/max telemetry ms for fleet map rows after race/leg filtering (matches MapTimeSeries x-domain). */
export function getFleetRaceLegFilteredBoundsMs(
  points: unknown[],
  options: {
    races: string[];
    legs: string[];
    trainingHourMode?: boolean;
  }
): { startMs: number; endMs: number } | null {
  const filtered = filterFleetMapPointsByRaceAndLeg(points, options);
  if (!Array.isArray(filtered) || filtered.length === 0) {return null;}

  let minMs = Infinity;
  let maxMs = -Infinity;
  for (const row of filtered) {
    const dt = extractFleetRowDatetime(row);
    if (!dt) {continue;}
    const t = dt.getTime();
    if (!Number.isFinite(t)) {continue;}
    minMs = Math.min(minMs, t);
    maxMs = Math.max(maxMs, t);
  }
  if (!Number.isFinite(minMs) || !Number.isFinite(maxMs) || maxMs < minMs) {return null;}
  return { startMs: minMs, endMs: maxMs };
}

export function legNumberFromExploreTags(
  tags: Record<string, unknown> | null | undefined
): number | null {
  if (tags == null || typeof tags !== 'object') {return null;}
  const raw = tags.Leg_number ?? tags.leg_number;
  if (raw == null) {return null;}
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) {return null;}
  return Math.trunc(n);
}

export type FleetTagRowForBounds = {
  kind: string;
  event_type: string;
  start_time: string | null;
  end_time: string | null;
  tags: unknown;
};

function parseUtcMsForBounds(iso: string | null | undefined): number | null {
  if (iso == null || String(iso).trim() === '') {return null;}
  const t = new Date(String(iso).trim()).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * Race/leg interval from explore tag rows (RACE/LEG dataset_events).
 * Preferred over telemetry extent — matches official event start_time/end_time.
 */
export function getFleetRaceLegBoundsFromTagRows(
  rows: FleetTagRowForBounds[],
  races: string[],
  legs: string[]
): { startMs: number; endMs: number } | null {
  if ((!Array.isArray(races) || races.length === 0) && (!Array.isArray(legs) || legs.length === 0)) {
    return null;
  }

  let minMs = Infinity;
  let maxMs = -Infinity;
  let matched = false;

  for (const row of rows) {
    if (row.kind !== 'dataset_event') {continue;}
    const et = (row.event_type || '').trim().toUpperCase();
    const tags = parseDatasetEventTags(row.tags);

    if (races.length > 0 && legs.length > 0) {
      if (et !== 'LEG') {continue;}
      const ln = legNumberFromExploreTags(tags);
      const rn = raceNumberFromExploreTags(tags, et);
      const legOk = ln != null && legs.some((l) => Number(l) === ln);
      const raceOk = rn != null && races.some((r) => isSameRace(r, rn));
      if (!legOk || !raceOk) {continue;}
    } else if (races.length > 0) {
      if (et !== 'RACE') {continue;}
      const rn = raceNumberFromExploreTags(tags, et);
      if (rn == null || !races.some((r) => isSameRace(r, rn))) {continue;}
    } else if (legs.length > 0) {
      if (et !== 'LEG') {continue;}
      const ln = legNumberFromExploreTags(tags);
      if (ln == null || !legs.some((l) => Number(l) === ln)) {continue;}
    }

    const st = parseUtcMsForBounds(row.start_time);
    const en = parseUtcMsForBounds(row.end_time) ?? st;
    if (st == null) {continue;}
    matched = true;
    minMs = Math.min(minMs, st);
    maxMs = Math.max(maxMs, en ?? st);
  }

  if (!matched || !Number.isFinite(minMs) || !Number.isFinite(maxMs) || maxMs < minMs) {
    return null;
  }
  return { startMs: minMs, endMs: maxMs };
}
